import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../client';
import type {
  MetadataFieldType,
  MetadataCategory,
  MetadataCategoryDropdown,
  MetadataSchemaListItem,
  MetadataSchemaDropdown,
  MetadataSchema,
  MetadataSchemaSaveRequest,
  CloneSchemaRequest,
  AssetMetadata,
  SaveAssetMetadataRequest,
  MetadataSearchCriteria,
  MetadataHistoryEntry,
} from '@/types/metadata.types';

// Query keys factory
export const metadataKeys = {
  all: ['metadata'] as const,
  fieldTypes: () => [...metadataKeys.all, 'fieldTypes'] as const,
  categories: () => [...metadataKeys.all, 'categories'] as const,
  categoryList: (activeOnly: boolean) => [...metadataKeys.categories(), { activeOnly }] as const,
  categoryDropdown: () => [...metadataKeys.categories(), 'dropdown'] as const,
  categoryDetail: (id: number) => [...metadataKeys.categories(), id] as const,
  schemas: () => [...metadataKeys.all, 'schemas'] as const,
  schemaList: (activeOnly: boolean) => [...metadataKeys.schemas(), { activeOnly }] as const,
  schemaDropdown: () => [...metadataKeys.schemas(), 'dropdown'] as const,
  schemaDetail: (id: number) => [...metadataKeys.schemas(), id] as const,
  schemaByCode: (code: string) => [...metadataKeys.schemas(), 'code', code] as const,
  schemaForAssetClass: (assetClassId: number) => [...metadataKeys.schemas(), 'assetClass', assetClassId] as const,
  schemasForAssetClass: (assetClassId: number) => [...metadataKeys.schemas(), 'assetClass', assetClassId, 'all'] as const,
  assetMetadataHistory: (assetId: string) => [...metadataKeys.all, 'history', assetId] as const,
  assetMetadata: (assetId: string) => [...metadataKeys.all, 'asset', assetId] as const,
};

// ================================
// Field Types
// ================================

export function useMetadataFieldTypes() {
  return useQuery({
    queryKey: metadataKeys.fieldTypes(),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: MetadataFieldType[] }>('/api/metadata/field-types');
      return response.data.data;
    },
    staleTime: 1000 * 60 * 60, // Field types rarely change, cache for 1 hour
  });
}

// ================================
// Categories
// ================================

export function useMetadataCategories(activeOnly: boolean = true) {
  return useQuery({
    queryKey: metadataKeys.categoryList(activeOnly),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: MetadataCategory[] }>(
        `/api/metadata/categories?activeOnly=${activeOnly}`
      );
      return response.data.data;
    },
  });
}

export function useMetadataCategoriesDropdown() {
  return useQuery({
    queryKey: metadataKeys.categoryDropdown(),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: MetadataCategoryDropdown[] }>(
        '/api/metadata/categories/dropdown'
      );
      return response.data.data;
    },
  });
}

export function useMetadataCategory(id: number, enabled: boolean = true) {
  return useQuery({
    queryKey: metadataKeys.categoryDetail(id),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: MetadataCategory }>(`/api/metadata/categories/${id}`);
      return response.data.data;
    },
    enabled: enabled && id > 0,
  });
}

export function useSaveMetadataCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (category: MetadataCategory) => {
      const response = await apiClient.post<{ success: boolean; data: { id: number } }>(
        '/api/metadata/categories',
        category
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: metadataKeys.categories() });
    },
  });
}

export function useDeleteMetadataCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/metadata/categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: metadataKeys.categories() });
    },
  });
}

export function useCreateDefaultCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiClient.post('/api/metadata/categories/create-defaults');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: metadataKeys.categories() });
    },
  });
}

// ================================
// Schemas
// ================================

export function useMetadataSchemas(activeOnly: boolean = true) {
  return useQuery({
    queryKey: metadataKeys.schemaList(activeOnly),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: MetadataSchemaListItem[] }>(
        `/api/metadata/schemas?activeOnly=${activeOnly}`
      );
      return response.data.data;
    },
  });
}

export function useMetadataSchemasDropdown() {
  return useQuery({
    queryKey: metadataKeys.schemaDropdown(),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: MetadataSchemaDropdown[] }>(
        '/api/metadata/schemas/dropdown'
      );
      return response.data.data;
    },
  });
}

export function useMetadataSchema(id: number, enabled: boolean = true) {
  return useQuery({
    queryKey: metadataKeys.schemaDetail(id),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: MetadataSchema }>(`/api/metadata/schemas/${id}`);
      return response.data.data;
    },
    enabled: enabled && id > 0,
  });
}

export function useMetadataSchemaByCode(code: string, enabled: boolean = true) {
  return useQuery({
    queryKey: metadataKeys.schemaByCode(code),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: MetadataSchema }>(`/api/metadata/schemas/code/${code}`);
      return response.data.data;
    },
    enabled: enabled && !!code,
  });
}

export function useMetadataSchemaForAssetClass(assetClassId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: metadataKeys.schemaForAssetClass(assetClassId),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: MetadataSchema }>(
        `/api/metadata/schemas/asset-class/${assetClassId}`
      );
      return response.data.data;
    },
    enabled: enabled && assetClassId > 0,
  });
}

/**
 * Feature 1: fetch ALL metadata schemas linked to an AssetClass (M:N).
 * Returns them in render order (junction SortOrder, then schema name).
 */
export function useMetadataSchemasForAssetClass(assetClassId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: metadataKeys.schemasForAssetClass(assetClassId),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: { schemas: MetadataSchema[] } }>(
        `/api/metadata/schemas/asset-class/${assetClassId}/all`
      );
      return response.data.data.schemas ?? [];
    },
    enabled: enabled && assetClassId > 0,
  });
}

export function useSaveMetadataSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (schema: MetadataSchemaSaveRequest) => {
      const response = await apiClient.post<{ success: boolean; data: { id: number } }>('/api/metadata/schemas', schema);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: metadataKeys.schemas() });
    },
  });
}

export function useDeleteMetadataSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/metadata/schemas/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: metadataKeys.schemas() });
    },
  });
}

export function useCloneMetadataSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, request }: { id: number; request: CloneSchemaRequest }) => {
      const response = await apiClient.post<{ success: boolean; data: { id: number } }>(
        `/api/metadata/schemas/${id}/clone`,
        request
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: metadataKeys.schemas() });
    },
  });
}

export function useExportMetadataSchema() {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await apiClient.get<{ success: boolean; data: string }>(`/api/metadata/schemas/${id}/export`);
      return response.data.data;
    },
  });
}

export function useImportMetadataSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jsonSchema: string) => {
      const response = await apiClient.post<{ success: boolean; data: { id: number } }>('/api/metadata/schemas/import', {
        jsonSchema,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: metadataKeys.schemas() });
    },
  });
}

// ================================
// Asset Metadata Values
// ================================

export function useAssetMetadata(assetId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: metadataKeys.assetMetadata(assetId),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: AssetMetadata }>(`/api/metadata/assets/${assetId}`);
      return response.data.data;
    },
    enabled: enabled && !!assetId,
  });
}

export function useSaveAssetMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SaveAssetMetadataRequest) => {
      await apiClient.post(`/api/metadata/assets/${request.assetId}`, request);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: metadataKeys.assetMetadata(variables.assetId) });
    },
  });
}

export function useDeleteAssetMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assetId: string) => {
      await apiClient.delete(`/api/metadata/assets/${assetId}`);
    },
    onSuccess: (_, assetId) => {
      queryClient.invalidateQueries({ queryKey: metadataKeys.assetMetadata(assetId) });
    },
  });
}

export function useSearchAssetsByMetadata() {
  return useMutation({
    mutationFn: async ({ criteria, limit }: { criteria: MetadataSearchCriteria; limit?: number }) => {
      const url = limit ? `/api/metadata/search?limit=${limit}` : '/api/metadata/search';
      const response = await apiClient.post<{ success: boolean; data: string[] }>(url, criteria);
      return response.data.data;
    },
  });
}

// ================================
// Metadata History
// ================================

export function useAssetMetadataHistory(assetId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: metadataKeys.assetMetadataHistory(assetId),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: MetadataHistoryEntry[] }>(
        `/api/metadata/assets/${assetId}/history`
      );
      return response.data.data;
    },
    enabled: enabled && !!assetId,
  });
}
